import { useState } from "react";
import {
  collection,
  addDoc,
  getDocs,
  updateDoc,
  doc,
  getDoc,
  serverTimestamp,
  query,
  where,
  orderBy,
} from "firebase/firestore";
import { db } from "../firebase";
import Handlebars from "handlebars";
import { flattenFlexSubmissionData, SubmissionData } from "./flexFormUtils";

export interface MappingConfig {
  labelTemplate: string;
  valueTemplate: string;
  metadata?: Record<string, { label: string; template: string }>;
}

export interface OptionMetadataValue {
  label: string;
  value: string;
}

export interface OptionItem {
  label: string;
  value: string;
  metadata?: Record<string, OptionMetadataValue> | null;
}

export interface OptionUpdateItem extends OptionItem {
  previous?: OptionItem;
  labelChanged?: boolean;
  metadataChanged?: boolean;
}

export interface SyncDiffResult {
  toAdd: OptionItem[];
  toUpdate: OptionUpdateItem[];
  toPrune: OptionItem[];
  unchanged: OptionItem[];
  isZeroSubmissionBlocked: boolean;
  authoritativeOptions: OptionItem[];
  totalSubmissions: number;
  totalUniqueSubmissions?: number;
}

export interface CreateOrSyncParams {
  name: string;
  templateId: string;
  mappingConfig: MappingConfig;
  companyId: string;
  collectionName?: string;
  overrideZeroGuard?: boolean;
  autoSync?: boolean;
}

export interface UseFormOptionsSetBuilderResult {
  loading: boolean;
  error: string | null;
  buildOptionsFromSubmissions: (submissions: SubmissionData[], mappingConfig: MappingConfig) => OptionItem[];
  calculateSyncDiff: (
    existingOptions: OptionItem[] | { options?: OptionItem[] },
    submissions: SubmissionData[],
    mappingConfig: MappingConfig
  ) => SyncDiffResult;
  executeSync: (
    optionsDocId: string,
    diffResult: SyncDiffResult,
    overrideZeroGuard?: boolean,
    collectionName?: string
  ) => Promise<{ success: boolean; count: number }>;
  createOrSyncOptionsSet: (
    params: CreateOrSyncParams
  ) => Promise<{ success: boolean; action: string; docId: string; count: number; diff?: SyncDiffResult }>;
  syncExistingOptionsSet: (
    docId: string,
    companyId: string,
    collectionNameOrOverride?: string | boolean,
    overrideZeroGuard?: boolean
  ) => Promise<{ success: boolean; count: number; diff?: SyncDiffResult }>;
  fetchSubmissionsForTemplate: (templateId: string, companyId: string) => Promise<SubmissionData[]>;
  getSyncDiffForOptionsSet: (
    optionsDocId: string,
    companyId: string,
    collectionName?: string
  ) => Promise<{ diff: SyncDiffResult; docData: any }>;
  syncLinkedOptionsSetsForTemplate: (
    templateId: string,
    companyId: string,
    collectionName?: string
  ) => Promise<{ syncedCount: number; optionSetIds: string[] }>;
}

/**
 * Robust timestamp extractor supporting Firestore Timestamps, Dates, ISO strings, and epoch numbers.
 */
export function extractSubmissionTimestamp(dateOrSub: any): number {
  if (!dateOrSub) return 0;
  let val = dateOrSub;
  if (
    typeof dateOrSub === "object" &&
    !(dateOrSub instanceof Date) &&
    typeof dateOrSub.toMillis !== "function" &&
    typeof dateOrSub.toDate !== "function"
  ) {
    if ("submissionDate" in dateOrSub && dateOrSub.submissionDate !== undefined) {
      val = dateOrSub.submissionDate;
    } else if ("createdAt" in dateOrSub && dateOrSub.createdAt !== undefined) {
      val = dateOrSub.createdAt;
    } else if ("timestamp" in dateOrSub && dateOrSub.timestamp !== undefined) {
      val = dateOrSub.timestamp;
    }
  }

  if (val === null || val === undefined) return 0;
  if (typeof val.toMillis === "function") return val.toMillis();
  if (typeof val.toDate === "function") {
    const d = val.toDate();
    return d instanceof Date && !isNaN(d.getTime()) ? d.getTime() : 0;
  }
  if (val instanceof Date) return isNaN(val.getTime()) ? 0 : val.getTime();
  if (typeof val === "object") {
    const secs = val.seconds ?? val._seconds;
    const nanos = val.nanoseconds ?? val._nanoseconds ?? 0;
    if (typeof secs === "number") {
      return secs * 1000 + Math.floor(nanos / 1000000);
    }
  }
  if (typeof val === "number") {
    if (isNaN(val)) return 0;
    return val < 10000000000 ? val * 1000 : val;
  }
  if (typeof val === "string") {
    const trimmed = val.trim();
    if (/^\d+$/.test(trimmed)) {
      const num = Number(trimmed);
      return num < 10000000000 ? num * 1000 : num;
    }
    const parsed = Date.parse(trimmed);
    return isNaN(parsed) ? 0 : parsed;
  }
  return 0;
}

/**
 * Helper to compile a Handlebars template with error handling.
 */
export const compileTemplate = (templateStr: string | undefined, context: Record<string, any>): string => {
  try {
    if (!templateStr) return "";
    const template = Handlebars.compile(templateStr);
    return template(context).trim();
  } catch (err) {
    console.error("Error compiling Handlebars template:", templateStr, err);
    return "";
  }
};

/**
 * Normalizes metadata objects for strict equality comparisons.
 */
function normalizeMetadata(
  meta: Record<string, OptionMetadataValue> | null | undefined
): Record<string, OptionMetadataValue> {
  if (!meta) return {};
  const normalized: Record<string, OptionMetadataValue> = {};
  for (const [k, v] of Object.entries(meta)) {
    if (v && typeof v === "object") {
      normalized[k] = {
        label: String(v.label ?? k),
        value: String(v.value ?? ""),
      };
    }
  }
  return normalized;
}

/**
 * Compares two metadata records for equality.
 */
export function areMetadataEqual(
  metaA: Record<string, OptionMetadataValue> | null | undefined,
  metaB: Record<string, OptionMetadataValue> | null | undefined
): boolean {
  const normA = normalizeMetadata(metaA);
  const normB = normalizeMetadata(metaB);
  const keysA = Object.keys(normA);
  const keysB = Object.keys(normB);
  if (keysA.length !== keysB.length) return false;
  for (const key of keysA) {
    if (!normB[key]) return false;
    if (normA[key].value !== normB[key].value) return false;
    if (normA[key].label !== normB[key].label) return false;
  }
  return true;
}

/**
 * Map submissions to options array using mapping config with deterministic
 * latest-wins deduplication by option value.
 */
export const buildOptionsFromSubmissions = (
  submissions: SubmissionData[],
  mappingConfig: MappingConfig
): OptionItem[] => {
  if (!submissions || submissions.length === 0) return [];

  // Sort submissions deterministically: primary by submission timestamp descending (latest first),
  // secondary tie-breaker by submission ID descending.
  const sortedSubmissions = [...submissions].sort((a, b) => {
    const timeA = extractSubmissionTimestamp(a);
    const timeB = extractSubmissionTimestamp(b);
    if (timeB !== timeA) {
      return timeB - timeA;
    }
    return String(b.id || "").localeCompare(String(a.id || ""));
  });

  const seenValues = new Set<string>();
  const deduplicatedOptions: OptionItem[] = [];

  for (const sub of sortedSubmissions) {
    const context = flattenFlexSubmissionData(sub);
    const label = compileTemplate(mappingConfig.labelTemplate, context);
    const value = compileTemplate(mappingConfig.valueTemplate, context);

    // Skip empty labels or values
    if (label === "" || value === "") continue;

    // Latest submission wins deduplication: skip if value was already seen in a newer submission
    if (seenValues.has(value)) {
      continue;
    }
    seenValues.add(value);

    const metadata: Record<string, OptionMetadataValue> = {};
    if (mappingConfig.metadata) {
      Object.entries(mappingConfig.metadata).forEach(([metaKey, metaConfig]) => {
        if (metaConfig && metaConfig.template) {
          const metaVal = compileTemplate(metaConfig.template, context);
          metadata[metaKey] = {
            label: metaConfig.label || metaKey,
            value: metaVal,
          };
        }
      });
    }

    deduplicatedOptions.push({
      label,
      value,
      metadata: Object.keys(metadata).length > 0 ? metadata : null,
    });
  }

  return deduplicatedOptions.sort((a, b) => a.label.localeCompare(b.label));
};

/**
 * Pure calculation engine producing a dry-run diff between existing options and
 * authoritative submissions according to the mapping config.
 */
export const calculateSyncDiff = (
  existingOptions: OptionItem[] | { options?: OptionItem[] },
  submissions: SubmissionData[],
  mappingConfig: MappingConfig
): SyncDiffResult => {
  const rawExisting = Array.isArray(existingOptions)
    ? existingOptions
    : (existingOptions as any)?.options || [];

  const existingList: OptionItem[] = Array.isArray(rawExisting) ? rawExisting : [];
  const subsList: SubmissionData[] = Array.isArray(submissions) ? submissions : [];

  const totalSubmissions = subsList.length;

  // Build authoritative options using deterministic latest-wins deduplication
  const authoritativeOptions = buildOptionsFromSubmissions(subsList, mappingConfig);
  const totalUniqueSubmissions = authoritativeOptions.length;

  // Existing options map keyed by string value
  const existingMap = new Map<string, OptionItem>();
  existingList.forEach((opt) => {
    if (opt && opt.value !== undefined && opt.value !== null) {
      existingMap.set(String(opt.value), opt);
    }
  });

  // Authoritative options map keyed by string value
  const authoritativeMap = new Map<string, OptionItem>();
  authoritativeOptions.forEach((opt) => {
    if (opt && opt.value !== undefined && opt.value !== null) {
      authoritativeMap.set(String(opt.value), opt);
    }
  });

  const toAdd: OptionItem[] = [];
  const toUpdate: OptionUpdateItem[] = [];
  const unchanged: OptionItem[] = [];
  const toPrune: OptionItem[] = [];

  // Identify Additions, Updates, and Unchanged
  authoritativeOptions.forEach((authOpt) => {
    const existingOpt = existingMap.get(String(authOpt.value));
    if (!existingOpt) {
      toAdd.push(authOpt);
    } else {
      const labelChanged = existingOpt.label !== authOpt.label;
      const metadataChanged = !areMetadataEqual(existingOpt.metadata, authOpt.metadata);

      if (labelChanged || metadataChanged) {
        toUpdate.push({
          ...authOpt,
          previous: existingOpt,
          labelChanged,
          metadataChanged,
        });
      } else {
        unchanged.push(authOpt);
      }
    }
  });

  // Identify Prunes: options present in existingOptions that no longer exist in authoritative submissions
  existingList.forEach((existingOpt) => {
    if (existingOpt && !authoritativeMap.has(String(existingOpt.value))) {
      toPrune.push(existingOpt);
    }
  });

  // Sort diff arrays alphabetically by label for deterministic output
  toAdd.sort((a, b) => a.label.localeCompare(b.label));
  toUpdate.sort((a, b) => a.label.localeCompare(b.label));
  toPrune.sort((a, b) => a.label.localeCompare(b.label));
  unchanged.sort((a, b) => a.label.localeCompare(b.label));

  // Zero-Submission Guard: if the query returned 0 documents for a non-empty Option Set
  const isZeroSubmissionBlocked = totalSubmissions === 0 && existingList.length > 0;

  return {
    toAdd,
    toUpdate,
    toPrune,
    unchanged,
    isZeroSubmissionBlocked,
    authoritativeOptions,
    totalSubmissions,
    totalUniqueSubmissions,
  };
};

/**
 * Commits authoritative options from a diff result to Firestore.
 * Aborts if Zero-Submission Guard is active without an explicit override.
 */
export const executeSync = async (
  optionsDocId: string,
  diffResult: SyncDiffResult,
  overrideZeroGuard = false,
  collectionName = "flexFormSelectOptions"
): Promise<{ success: boolean; count: number }> => {
  if (diffResult.isZeroSubmissionBlocked && !overrideZeroGuard) {
    throw new Error("Zero submissions found for non-empty Option Set. Pruning aborted by Zero-Submission Guard.");
  }

  const docRef = doc(db, collectionName, optionsDocId);
  await updateDoc(docRef, {
    options: diffResult.authoritativeOptions,
    updatedAt: serverTimestamp(),
    lastSyncedAt: serverTimestamp(),
  });

  return { success: true, count: diffResult.authoritativeOptions.length };
};

/**
 * Fetches all submissions for a linked form template.
 */
export const fetchSubmissionsForTemplate = async (
  templateId: string,
  companyId: string
): Promise<SubmissionData[]> => {
  const qSubmissions = query(
    collection(db, "flexFormSubmissions"),
    where("templateId", "==", templateId),
    where("companyId", "==", companyId),
    orderBy("submissionDate", "desc")
  );
  const subSnapshot = await getDocs(qSubmissions);
  return subSnapshot.docs.map((d) => ({ id: d.id, ...d.data() } as unknown as SubmissionData));
};

/**
 * Fetches an Option Set document and calculates its diff against current submissions without modifying Firestore.
 */
export const getSyncDiffForOptionsSet = async (
  optionsDocId: string,
  companyId: string,
  collectionName = "flexFormSelectOptions"
): Promise<{ diff: SyncDiffResult; docData: any }> => {
  const docRef = doc(db, collectionName, optionsDocId);
  const docSnap = await getDoc(docRef);
  if (!docSnap.exists()) {
    throw new Error("Options set document not found.");
  }
  const docData = docSnap.data();
  const { linkedFormTemplateId, mappingConfig, options: existingOptions = [] } = docData;
  if (!linkedFormTemplateId || !mappingConfig) {
    throw new Error("This options set is not configured for automatic synchronization.");
  }
  const submissions = await fetchSubmissionsForTemplate(linkedFormTemplateId, companyId);
  const diff = calculateSyncDiff(existingOptions, submissions, mappingConfig);
  return { diff, docData };
};

/**
 * Synchronizes all linked Option Sets (`flexFormSelectOptions`) for a given form template
 * and company where `autoSync !== false`.
 */
export const syncLinkedOptionsSetsForTemplate = async (
  templateId: string,
  companyId: string,
  collectionName = "flexFormSelectOptions"
): Promise<{ syncedCount: number; optionSetIds: string[] }> => {
  if (!templateId || !companyId) {
    return { syncedCount: 0, optionSetIds: [] };
  }

  const qOptionSets = query(
    collection(db, collectionName),
    where("companyId", "==", companyId),
    where("linkedFormTemplateId", "==", templateId)
  );
  const optSnapshot = await getDocs(qOptionSets);
  if (!optSnapshot || optSnapshot.empty) {
    return { syncedCount: 0, optionSetIds: [] };
  }

  const linkedOptionSets = optSnapshot.docs
    .map((d) => ({ id: d.id, ...(d.data() as any) }))
    .filter(
      (docData) =>
        docData.autoSync !== false &&
        docData.mappingConfig &&
        typeof docData.mappingConfig.labelTemplate === "string" &&
        typeof docData.mappingConfig.valueTemplate === "string"
    );

  if (linkedOptionSets.length === 0) {
    return { syncedCount: 0, optionSetIds: [] };
  }

  const submissions = await fetchSubmissionsForTemplate(templateId, companyId);
  const syncedIds: string[] = [];

  for (const optSet of linkedOptionSets) {
    const diff = calculateSyncDiff(optSet.options || [], submissions, optSet.mappingConfig);
    if (diff.isZeroSubmissionBlocked) {
      continue;
    }
    const hasChanges =
      diff.toAdd.length > 0 || diff.toUpdate.length > 0 || diff.toPrune.length > 0;
    if (!hasChanges) {
      continue;
    }
    await executeSync(optSet.id, diff, false, collectionName);
    syncedIds.push(optSet.id);
  }

  return { syncedCount: syncedIds.length, optionSetIds: syncedIds };
};

export function useFormOptionsSetBuilder(): UseFormOptionsSetBuilderResult {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Create a new Options Set document or sync to an existing one using authoritative diff engine.
   */
  const createOrSyncOptionsSet = async ({
    name,
    templateId,
    mappingConfig,
    companyId,
    collectionName = "flexFormSelectOptions",
    overrideZeroGuard = false,
    autoSync = true,
  }: CreateOrSyncParams): Promise<{ success: boolean; action: string; docId: string; count: number; diff?: SyncDiffResult }> => {
    setLoading(true);
    setError(null);
    try {
      // 1. Fetch all active submissions for this template
      const submissions = await fetchSubmissionsForTemplate(templateId, companyId);

      // 2. Look for existing Options Set with same name
      const qExisting = query(
        collection(db, collectionName),
        where("companyId", "==", companyId),
        where("name", "==", name)
      );
      const existingSnapshot = await getDocs(qExisting);

      let targetDocId: string | null = null;
      let existingOptions: OptionItem[] = [];

      if (!existingSnapshot.empty) {
        const firstMatch = existingSnapshot.docs[0];
        targetDocId = firstMatch.id;
        existingOptions = firstMatch.data().options || [];
      }

      if (targetDocId) {
        // Authoritative true sync diff calculation
        const diff = calculateSyncDiff(existingOptions, submissions, mappingConfig);

        if (diff.isZeroSubmissionBlocked && !overrideZeroGuard) {
          throw new Error("Zero submissions found for non-empty Option Set. Pruning aborted by Zero-Submission Guard.");
        }

        await updateDoc(doc(db, collectionName, targetDocId), {
          options: diff.authoritativeOptions,
          linkedFormTemplateId: templateId,
          mappingConfig,
          autoSync: autoSync !== false,
          updatedAt: serverTimestamp(),
          lastSyncedAt: serverTimestamp(),
        });
        return { success: true, action: "sync", docId: targetDocId, count: diff.authoritativeOptions.length, diff };
      } else {
        // Create new document
        const diff = calculateSyncDiff([], submissions, mappingConfig);
        const docData = {
          name,
          companyId,
          linkedFormTemplateId: templateId,
          mappingConfig,
          autoSync: autoSync !== false,
          options: diff.authoritativeOptions,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
          lastSyncedAt: serverTimestamp(),
        };
        const docRef = await addDoc(collection(db, collectionName), docData);
        return { success: true, action: "create", docId: docRef.id, count: diff.authoritativeOptions.length, diff };
      }
    } catch (err: any) {
      console.error("Error creating/syncing options set:", err);
      setError(err.message || "Failed to create or sync options set.");
      throw err;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Direct Sync function for an existing Options Set doc using authoritative diff engine.
   */
  const syncExistingOptionsSet = async (
    docId: string,
    companyId: string,
    collectionNameOrOverride: string | boolean = "flexFormSelectOptions",
    overrideZeroGuardArg = false
  ): Promise<{ success: boolean; count: number; diff?: SyncDiffResult }> => {
    let collectionName = "flexFormSelectOptions";
    let overrideZeroGuard = false;

    if (typeof collectionNameOrOverride === "boolean") {
      overrideZeroGuard = collectionNameOrOverride;
    } else if (typeof collectionNameOrOverride === "string") {
      collectionName = collectionNameOrOverride;
      if (typeof overrideZeroGuardArg === "boolean") {
        overrideZeroGuard = overrideZeroGuardArg;
      }
    }

    setLoading(true);
    setError(null);
    try {
      const docRef = doc(db, collectionName, docId);
      const docSnap = await getDoc(docRef);
      if (!docSnap.exists()) {
        throw new Error("Options set document not found.");
      }

      const docData = docSnap.data();
      const { linkedFormTemplateId, mappingConfig, options: existingOptions = [] } = docData;

      if (!linkedFormTemplateId || !mappingConfig) {
        throw new Error("This options set is not configured for automatic synchronization.");
      }

      // Fetch submissions
      const submissions = await fetchSubmissionsForTemplate(linkedFormTemplateId, companyId);

      // Compute authoritative diff
      const diff = calculateSyncDiff(existingOptions, submissions, mappingConfig);

      // Execute sync with zero guard check
      await executeSync(docId, diff, overrideZeroGuard, collectionName);

      return { success: true, count: diff.authoritativeOptions.length, diff };
    } catch (err: any) {
      console.error("Error syncing existing options set:", err);
      setError(err.message || "Failed to sync options set.");
      throw err;
    } finally {
      setLoading(false);
    }
  };

  return {
    loading,
    error,
    buildOptionsFromSubmissions,
    calculateSyncDiff,
    executeSync,
    createOrSyncOptionsSet,
    syncExistingOptionsSet,
    fetchSubmissionsForTemplate,
    getSyncDiffForOptionsSet,
    syncLinkedOptionsSetsForTemplate,
  };
}
